import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Keyboard, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { ORG } from "../seo/site.js";
import { trackEvent } from "../utils/analytics.js";
import { langOf } from "../utils/lang.js";
import TamilDecor from "./TamilDecor.jsx";

// Homepage "Reviews on Google" section. Live data comes from
// public/google-reviews.php (Places API, key kept on the server). Reviews are
// shown exactly as Google returns them: never filtered by rating, edited or
// invented. Until the API key is configured - or if Google is unavailable -
// the section shows only the links to WSTSC's real Google listing.
// Deliberately no AggregateRating/Review structured data: Google doesn't allow
// review markup for an organisation's own reviews.

const Stars = ({ rating, size = "" }) => {
  const rounded = Math.round((rating || 0) * 2) / 2;
  return (
    <span className={`review-stars ${size}`} role="img" aria-label={`Rated ${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          aria-hidden="true"
          className={`bi ${rounded >= n ? "bi-star-fill" : rounded >= n - 0.5 ? "bi-star-half" : "bi-star"}`}
        ></i>
      ))}
    </span>
  );
};

// Compact "thumbnail" card for the carousel: reviewer photo, name, stars,
// date and an excerpt. Full reviews open on Google (keeps slides equal height).
const ReviewCard = ({ review }) => {
  const initial = review.author.trim().charAt(0).toUpperCase();

  return (
    <article className="review-card h-100 bg-white border rounded-4 border-bottom-left p-4 d-flex flex-column">
      <div className="d-flex align-items-center gap-3 mb-3">
        {review.authorPhoto ? (
          <img
            src={review.authorPhoto}
            alt=""
            width="48"
            height="48"
            loading="lazy"
            referrerPolicy="no-referrer"
            className="review-thumb rounded-circle flex-shrink-0"
          />
        ) : (
          <span className="review-thumb review-avatar rounded-circle flex-shrink-0" aria-hidden="true">
            {initial}
          </span>
        )}
        <div className="min-w-0">
          {review.authorUri ? (
            <a
              href={review.authorUri}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-semibold text-dark text-decoration-none d-block text-truncate"
            >
              {review.author}
              <span className="visually-hidden"> (Google profile, opens in a new tab)</span>
            </a>
          ) : (
            <span className="fw-semibold d-block text-truncate">{review.author}</span>
          )}
          <div className="small text-muted">{review.relativeTime}</div>
        </div>
        <i className="bi bi-google ms-auto text-muted flex-shrink-0" aria-hidden="true" title="Google review"></i>
      </div>
      <Stars rating={review.rating} />
      {review.text && (
        <p className="text-muted mt-2 mb-0 review-text is-clamped" lang={review.language || langOf(review.text)}>
          {review.text}
        </p>
      )}
      {review.reviewUri && (
        <a
          href={review.reviewUri}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto pt-3 red-text fw-semibold text-decoration-none small"
        >
          Read on Google
          <span className="visually-hidden">: review by {review.author} (opens in a new tab)</span>
          <i className="bi bi-box-arrow-up-right ms-1" aria-hidden="true"></i>
        </a>
      )}
    </article>
  );
};

const GoogleReviews = () => {
  const sectionRef = useRef(null);
  const [state, setState] = useState({ status: "idle", data: null });

  // Ask for reviews only when the section is close to the screen.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    let cancelled = false;

    const load = () => {
      setState({ status: "loading", data: null });
      fetch(ORG.googleListing.reviewsEndpoint, {
        headers: { Accept: "application/json" },
        signal: AbortSignal.timeout(10000),
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((data) => !cancelled && setState({ status: "ready", data }))
        .catch(() => !cancelled && setState({ status: "unavailable", data: null }));
    };

    if (!("IntersectionObserver" in window)) {
      load();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: "500px 0px" },
    );
    observer.observe(section);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  const place = state.data?.place;
  const reviews = state.data?.reviews || [];
  const readUrl = place?.reviewsUri || place?.mapsUri || ORG.googleListing.url;
  const writeUrl = place?.writeReviewUri || ORG.googleListing.url;
  const track = (action) => () => trackEvent("google_review_click", { action });

  return (
    <section
      id="reviews"
      ref={sectionRef}
      data-reviews-status={state.status}
      className="tabs-bg py-5"
      aria-labelledby="reviews-heading"
    >
      <TamilDecor />
      <div className="container py-4">
        <div className="text-center mb-4">
          <h2
            id="reviews-heading"
            className="h1 fw-bold lh-base mb-2"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            <span className="red-text">What Families </span>
            <span className="blue-text text-nowrap">Say About Us</span>
          </h2>

          {place?.rating && place.userRatingCount > 0 ? (
            <p className="d-inline-flex flex-wrap align-items-center justify-content-center gap-2 mb-0">
              <span className="fs-3 fw-bold">{Number(place.rating).toFixed(1)}</span>
              <Stars rating={place.rating} size="fs-5" />
              <span className="text-muted">
                {place.userRatingCount} Google review{place.userRatingCount === 1 ? "" : "s"}
              </span>
            </p>
          ) : (
            <p className="text-muted mb-0 col-lg-7 mx-auto">
              Families share their experiences of WSTSC on Google. Read their
              reviews, or tell other parents about yours.
            </p>
          )}
        </div>

        {state.status === "loading" && (
          <div className="text-center py-4">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading Google reviews…</span>
            </div>
          </div>
        )}

        {reviews.length > 0 && (
          <div className="reviews-carousel position-relative">
            <Swiper
              modules={[Navigation, Pagination, Keyboard, A11y]}
              slidesPerView={1}
              spaceBetween={20}
              breakpoints={{ 768: { slidesPerView: 2, spaceBetween: 24 }, 992: { slidesPerView: 3, spaceBetween: 24 } }}
              navigation={{ prevEl: ".reviews-prev", nextEl: ".reviews-next" }}
              pagination={{ el: ".reviews-pagination", clickable: true }}
              keyboard={{ enabled: true, onlyInViewport: true }}
              a11y={{
                prevSlideMessage: "Previous review",
                nextSlideMessage: "Next review",
                paginationBulletMessage: "Go to review {{index}}",
              }}
              watchOverflow
              className="pb-1"
            >
              {reviews.map((review, index) => (
                <SwiperSlide key={`${review.publishTime}-${index}`}>
                  <ReviewCard review={review} />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
              <button type="button" className="reviews-prev custom-arrow-indicator review-arrow d-flex align-items-center justify-content-center bg-transparent" aria-label="Previous review">
                <i className="bi bi-chevron-left" aria-hidden="true"></i>
              </button>
              <div className="reviews-pagination d-flex justify-content-center gap-1"></div>
              <button type="button" className="reviews-next custom-arrow-indicator review-arrow d-flex align-items-center justify-content-center bg-transparent" aria-label="Next review">
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        )}

        <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-center gap-3 mt-4">
          <a
            href={readUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={track("read")}
            className="btn review-btn rounded-5 px-4 py-2 fw-semibold d-inline-flex align-items-center justify-content-center"
          >
            <i className="bi bi-google me-2" aria-hidden="true"></i>
            {reviews.length > 0 ? "See all reviews on Google" : "See our reviews on Google"}
            <span className="visually-hidden"> (opens in a new tab)</span>
          </a>
          <a
            href={writeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={track("write")}
            className="btn blue-bg text-white rounded-5 px-4 py-2 fw-semibold d-inline-flex align-items-center justify-content-center"
          >
            <i className="bi bi-pencil-square me-2" aria-hidden="true"></i>
            Write a review
            <span className="visually-hidden"> (opens Google in a new tab)</span>
          </a>
        </div>

        {reviews.length > 0 && (
          <p className="small text-muted text-center mt-3 mb-0">
            Reviews from {state.data.attribution || "Google Maps"}. Shown as
            written by reviewers.
          </p>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
